<script setup lang="ts">
import type { TenantLandingConfig, TenantBrandingConfig } from '~/types/types'

/**
 * Stats block, shared across landing templates.
 *  - grid: centered cells on a tinted band (Classic)
 *  - row:  compact inline row, left-aligned (Split / Editorial)
 *  - big:  oversized numerals, display face (Bold)
 */
withDefaults(defineProps<{
  landing: TenantLandingConfig
  branding: TenantBrandingConfig
  variant?: 'grid' | 'row' | 'big'
  displayClass?: string
}>(), { variant: 'grid', displayClass: '' })
</script>

<template>
  <template v-if="landing.stats_visible && landing.stats.length">
    <section
      v-if="variant === 'grid'"
      class="border-b border-[var(--border)] bg-[var(--bg2)]"
    >
      <div class="mx-auto max-w-6xl px-6 py-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
        <div
          v-for="(stat, i) in landing.stats"
          :key="i"
          class="text-center"
        >
          <p
            class="text-3xl font-bold site-text-accent"
          >
            {{ stat.value }}
          </p>
          <p class="text-sm ui-text-subtle mt-1">
            {{ stat.label }}
          </p>
        </div>
      </div>
    </section>

    <div
      v-else-if="variant === 'row'"
      class="flex flex-wrap gap-x-10 gap-y-4"
    >
      <div
        v-for="(stat, i) in landing.stats"
        :key="i"
      >
        <p
          class="text-2xl font-bold site-text-accent"
        >
          {{ stat.value }}
        </p>
        <p class="text-xs uppercase tracking-wide ui-text-subtle mt-0.5">
          {{ stat.label }}
        </p>
      </div>
    </div>

    <section
      v-else
      class="border-y border-[var(--border)]"
    >
      <div class="mx-auto max-w-6xl px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
        <div
          v-for="(stat, i) in landing.stats"
          :key="i"
        >
          <p
            class="text-5xl sm:text-6xl font-black tracking-tight site-text-accent"
            :class="displayClass"
          >
            {{ stat.value }}
          </p>
          <p class="text-sm uppercase tracking-widest ui-text-subtle mt-2">
            {{ stat.label }}
          </p>
        </div>
      </div>
    </section>
  </template>
</template>
